import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "./axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

export const companiesKeys = {
  all: () => ["companies"] as const,
  companie: () => ["companie"] as const,
  companieByID: (id: number) =>
    [...companiesKeys.companie(), "id", id] as const,
  companieEmployee: (idCompanie: number) =>
    ["companieEmployee", "id", idCompanie] as const,
  deletionImpact: (id: number) =>
    [...companiesKeys.companieByID(id), "deletion-impact"] as const,
};

/**
 * A duplicate registration_id surfaces as a constraint violation. The API used
 * to leak the driver's SQLSTATE ("23000") straight through; it now returns a
 * stable symbolic code, so accept either.
 */
const isUniqueViolation = (err: any): boolean => {
  const code = err?.response?.data?.code;
  return code === "CONSTRAINT_VIOLATION" || code === "23000" || code === 23000;
};

/**
 * `is_active` is a char column: the list endpoint returns "1"/"0", the impact
 * endpoint casts to a real boolean. `!companie.is_active` is therefore wrong
 * for archived companies — the string "0" is truthy. Normalise in one place.
 *
 * Absent (undefined) is treated as active: an older API build that does not
 * send the field should not make every company look archived.
 */
export const isCompanyArchived = (
  value: boolean | number | string | undefined | null
): boolean => {
  if (value === undefined || value === null) return false;
  return value === false || value === 0 || value === "0";
};

/**
 * What the API says would happen if this company were deleted.
 *
 * `blockers` are business/legal records (vehicles, rental contracts, invoices)
 * that make a hard delete impossible — the company must be archived instead.
 * `collateral` are transient rows (sessions, memberships, invitations) that
 * would be removed along with it.
 */
export type TDeletionImpact = {
  company_id: number;
  company_name: string;
  is_active: boolean;
  can_purge: boolean;
  blockers: Record<string, number>;
  collateral: Record<string, number>;
  unmapped: string[];
};

export const useGetAllCompanies = () => {
  const { data, error, ...res } = useQuery({
    queryKey: companiesKeys.all(),
    queryFn: async (): Promise<TCompanie[]> => {
      const data = await axios.get(`/admin/companies`);
      return data?.data?.companies;
    },
  });
  if (error) {
  }
  return { companies: data ?? [], ...res };
};

export const useGetOneCompanie = (id: number) => {
  const { data, error, ...res } = useQuery({
    queryKey: companiesKeys.companieByID(id),
    queryFn: async (): Promise<TCompanie> => {
      const data = await axios.get(`/admin/companies/${id}`);
      return data.data;
    },
    enabled: !Number.isNaN(id),
  });
  if (error) {
  }
  return { companie: data, ...res };
};

export interface RequestCompanie {
  id?: number;
  name: string;
  registration_id: string;
  logo: string;
  is_fleet: number;
  is_rental: number;
}

export const useCreateCompanie = (
  onClose?: () => void,
  initCompanie?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (newCompanie: RequestCompanie) => {
      const data = await axios.post("/admin/companies", newCompanie);
      return data.data;
    },
    onSuccess: (data) => {
      onClose && onClose();
      initCompanie && initCompanie();
      const keys = companiesKeys.all();
      const prev = queryClient.getQueryData<TCompanie[]>(keys);
      if (prev && data) {
        queryClient.setQueryData<TCompanie[]>(keys, [...prev, data]);
      }
      toast.success(t("compagnies.addSuccessful", "Change saved"));
    },
    onError: (err: any) => {
      // The API no longer echoes raw SQLSTATE codes; "23000" is kept so a
      // client deployed ahead of the API still behaves.
      if (isUniqueViolation(err)) {
        toast.error(t("compagnies.alreadyExist", "Companie already exists"));
      } else {
        toast.error(
          err?.response?.data?.message ??
            t("common.error", "Something went wrong")
        );
      }
    },
  });
};

export const useUpdateCompanie = (
  onClose?: () => void,
  initCompanie?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (newCompanie: RequestCompanie) => {
      const data = await axios.put(
        `/admin/companies/${newCompanie.id}`,
        newCompanie
      );
      return data.data;
    },
    onSuccess: (data) => {
      onClose && onClose();
      initCompanie && initCompanie();
      const keys = companiesKeys.all();
      const prev = queryClient.getQueryData<TCompanie[]>(keys);
      if (prev && data) {
        const res = prev.map((p) => {
          if (p.id === data.id) {
            return data;
          }
          return p;
        });
        queryClient.setQueryData<TCompanie[]>(keys, res);
      }
      toast.success(t("compagnies.updateSuccess", "Change saved"));
    },
    onError: (err: any) => {
      if (isUniqueViolation(err)) {
        toast.error(t("compagnies.alreadyExist", "Companie already exists"));
      } else {
        toast.error(
          err?.response?.data?.message ??
            t("common.error", "Something went wrong")
        );
      }
    },
  });
};

/**
 * Dry run. Only fetched while the delete dialog is actually open, so the list
 * page doesn't fire one of these per row.
 */
export const useCompanyDeletionImpact = (id: number, enabled: boolean) => {
  const { data, ...res } = useQuery({
    queryKey: companiesKeys.deletionImpact(id),
    queryFn: async (): Promise<TDeletionImpact> => {
      const response = await axios.get(
        `/admin/companies/${id}/deletion-impact`
      );
      return response.data;
    },
    enabled: enabled && !Number.isNaN(id),
    staleTime: 0,
  });
  return { impact: data, ...res };
};

/**
 * Reversible. This is the action the delete dialog should push people toward
 * whenever the company holds anything at all.
 */
export const useArchiveCompanie = (
  idCompanie: number,
  onClose?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const data = await axios.post(`/admin/companies/${idCompanie}/archive`);
      return data.data;
    },
    onSuccess: () => {
      onClose && onClose();
      queryClient.invalidateQueries({ queryKey: companiesKeys.all() });
      queryClient.invalidateQueries({
        queryKey: companiesKeys.deletionImpact(idCompanie),
      });
      toast.success(
        t("compagnies.archiveSuccess", "Company archived")
      );
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ?? t("common.error", "Something went wrong")
      );
    },
  });
};

export const useRestoreCompanie = (
  idCompanie: number,
  onClose?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const data = await axios.post(`/admin/companies/${idCompanie}/restore`);
      return data.data;
    },
    onSuccess: () => {
      onClose && onClose();
      queryClient.invalidateQueries({ queryKey: companiesKeys.all() });
      toast.success(t("compagnies.restoreSuccess", "Company restored"));
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ?? t("common.error", "Something went wrong")
      );
    },
  });
};

/**
 * Irreversible. The API requires the exact company name as ?confirm=, so the
 * mutation takes the string the operator typed rather than an id.
 */
export const useDeleteCompanie = (
  idCompanie: number,
  onClose?: () => void,
  initCompanie?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (confirmName: string) => {
      const data = await axios.delete(`/admin/companies/${idCompanie}`, {
        params: { confirm: confirmName },
      });
      return data.data;
    },
    onSuccess: (_) => {
      onClose && onClose();
      initCompanie && initCompanie();

      const keys = companiesKeys.all();
      const prev = queryClient.getQueryData<TCompanie[]>(keys);
      if (prev) {
        const res = prev.filter((companie) => companie.id !== idCompanie);
        queryClient.setQueryData<TCompanie[]>(keys, res);
      }
      toast.success(t("compagnies.deleteSuccess", "Change saved"));
    },
    onError: (err: any) => {
      const payload = err?.response?.data;

      // The API refused because the company still owns real records. Name them
      // rather than showing a generic failure.
      if (payload?.code === "COMPANY_HAS_DEPENDENTS") {
        const blockers: Record<string, number> = payload?.impact?.blockers ?? {};
        const list = Object.entries(blockers)
          .map(([label, count]) => `${count} ${label.replace(/_/g, " ")}`)
          .join(", ");
        toast.error(
          t("compagnies.hasDependents", {
            list,
            defaultValue: `Cannot delete: this company still holds ${list}. Archive it instead.`,
          })
        );
        return;
      }

      toast.error(
        payload?.message ?? t("common.error", "Something went wrong")
      );
    },
  });
};

export const useGetAllCompanieEmployee = (idCompanie: number) => {
  const { data, error, ...res } = useQuery({
    queryKey: companiesKeys.companieEmployee(idCompanie),
    queryFn: async (): Promise<TCompanieEmployee[]> => {
      const data = await axios.get(`/companies/${idCompanie}/employees`);
      return data.data.users;
    },
  });
  if (error) {
  }
  return { employees: data ?? [], ...res };
};

export enum UserRole {
  SUPER_ADMIN = "SUPER_ADMIN",
  FLEET_MANAGER = "FLEET_MANAGER",
  EMPLOYEE = "EMPLOYEE",
}

interface RequestInviteEmployee {
  role: UserRole;
  email: string;
  firstname: string;
  lastname: string;
}

export const useInviteEmployeeCompanie = (
  idCompanie: number,
  onClose?: () => void
) => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (user: RequestInviteEmployee) => {
      const data = await axios.post(
        `/companies/${idCompanie}/employees/invitation`,
        user
      );
      return data.data;
    },
    onSuccess: (_) => {
      onClose && onClose();
      toast.success(t("common.sendInvitation", "Invitation send"));
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message);
    },
  });
};

export const useChangeRoleEmployeeCompanie = (
  idCompanie: number,
  idUser: number
) => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (user: RequestInviteEmployee) => {
      const data = await axios.post(
        `/companies/${idCompanie}/employees/${idUser}/change-role`,
        user
      );
      return data.data;
    },
    onSuccess: (_) => {
      toast.success(t("common.changeRole", "The role has been changed"));
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message);
    },
  });
};
